<template>
    <div class="scene3d" :style="sceneStyle()">
        <div class="env3d" :style="envStyle()">
            <div class="container3d">

                <div class="absolute top-[-225px] md:top-[-240px] left-[-115px] md:left-[-150px] border-l border-black h-10 md:h-16 pl-2 text-sm">
                    {{ year }}
                </div>

                <Molecule3DCard
                    v-for="(card, index) in cards"
                    :key="index"
                    :card="card"
                    :card-style="cardStyle(card)"
                    :stack-position="stackPosition"
                    :is-current="index === currentCardIndex"
                    @click.native="handleCardClick"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Molecule3DCard from "../molecules/Molecule3DCard.vue";

export default {
    name: 'Organism3DStack',
    components: {Molecule3DCard},
    emits: ['next-stack', 'previous-stack', 'current-card-index-change', 'card-click'],

    props: {
        cards: { type: Array, default: false },
        year: { type: Number, required: true },
        stackPosition: { type: Number, required: true },
        isActive: { type: Boolean, default: false },
        isLast: { type: Boolean, default: false },
        config: { type: Object, default: null }
    },

    data(){
        return{
            windowWidth: window.innerWidth,
            currentCardIndex: 0,
        }
    },

    watch: {
        currentCardIndex(newIndex) {
            this.$emit('current-card-index-change', newIndex);
        },
    },

    methods: {
        handleMouseScroll(event, delta = null) {
            if (!this.isActive) return;

            // Determine the scroll direction (delta)
            const direction = delta || event.deltaY;

            if (direction > 0){
                this.incrementCurrentCardIndex()
            }else {
                this.decrementCurrentCardIndex()
            }

            this.updateCardsPositions(this.currentCardIndex);
        },

        updateCardsPositions(currentIndex){
            this.currentCardIndex = currentIndex

            this.cards.forEach((card, index) => {
                card.positionZ = (index - this.currentCardIndex) * -this.config.cardDeltaZ
                if (card.positionZ > 0){
                    card.positionX = -1000
                }else {
                    card.positionX = 0
                }
            });
        },

        incrementCurrentCardIndex() {
            if (this.isActive && this.currentCardIndex < this.cards.length - 1) {
                this.currentCardIndex++;
                this.updateCardsPositions(this.currentCardIndex);
            }else {
                this.$emit('next-stack')
            }
        },

        decrementCurrentCardIndex() {
            if (this.isActive && this.currentCardIndex > 0) {
                this.currentCardIndex--;
                this.updateCardsPositions(this.currentCardIndex);
            }else {
                this.$emit('previous-stack')
            }
        },

        handleCardClick() {
            if (!this.isActive){
                this.$emit('card-click')
            }else {
                const currentCard = this.cards[this.currentCardIndex]
                this.$router.push({name: 'event.view', params: {id: currentCard.id, slug: currentCard.slug}})
            }
        },

        // Update scene style when resize window
        handleWindowResize() {
            this.windowWidth = window.innerWidth;
            this.sceneStyle();
        },

        handleKeyDown(event) {
            // Check if the pressed key is the down arrow key
            if (event.keyCode === 40 && this.isActive) {
                this.decrementCurrentCardIndex()
            }
            // Check if the pressed key is the up arrow key
            if (event.keyCode === 38 && this.isActive) {
                this.incrementCurrentCardIndex()
            }
        },

        // Computed style for the scene in function of the windows size and the current stack position
        sceneStyle() {
            const { windowWidth, stackPosition } = this;
            const perspectiveX = 120 - stackPosition * 10;
            const perspectiveY = 20 - stackPosition * 10;

            // Mobile
            if (windowWidth <= 800) {
                // Display none out of view for better perf
                if (stackPosition > 1) return 'display: none';
                if (stackPosition < -1) return 'display: none';

                if (stackPosition === 0) {
                    return 'left: 0%; bottom: 15%; perspective-origin: 120% 30%; width: 100%';
                } else if (stackPosition > 0) {
                    return 'left: 100%; bottom: 15%; width: 100%';
                } else if (stackPosition < 0) {
                    return 'left: -150%; bottom: 15%; width: 100%';
                }
            }

            // Tablet
            if (windowWidth <= 1200) {
                // Display none out of view for better perf
                if (stackPosition > 2) return 'display: none';
                if (stackPosition < -1) return 'display: none';

                if (stackPosition > 1) {
                    return 'left: 150%; bottom: 20%';
                }
                if (stackPosition < 0) {
                    return 'left: -150%; bottom: 20%';
                }
                const leftPos = 50 * stackPosition;
                return `perspective-origin: ${perspectiveX}% ${perspectiveY}%; left: ${leftPos}%; width: 50%; bottom: ${10 * stackPosition}%`;
            }

            // Display none out of view for better perf
            if (stackPosition > 4) return 'display: none';
            if (stackPosition < -2) return 'display: none';

            const leftPos = stackPosition < 0 ? -100 : stackPosition > 2 ? 150 : 33 * stackPosition;
            return `perspective-origin: ${perspectiveX}% ${perspectiveY}%; left: ${leftPos}%`;
        },

        // Computed style for the env in function of the current stack position
        envStyle() {
            const bottomPercentage = 35 + this.stackPosition * 15;
            const leftPercentage = this.stackPosition === 1 ? 55 : 45;
            return `bottom: ${bottomPercentage}%; left: ${leftPercentage}%;`;
        },

        // Computed card style
        cardStyle(card) {
            if (card.positionZ < -this.config.cardDeltaZ * this.config.visibleStackCard){
                return "display: none;"
            }
            if (card.positionZ < -this.config.cardDeltaZ * (this.config.visibleStackCard-1)){
                return `transform: rotateZ(0deg) rotateY(0deg) translateZ(${card.positionZ}px) translateX(${card.positionX}px) translateY(0px); opacity: 0`;
            }
            return `transform: rotateZ(0deg) rotateY(0deg) translateZ(${card.positionZ}px) translateX(${card.positionX}px) translateY(0px); opacity: 1`;
        },
    },
}
</script>

<style lang="scss">
.scene3d {
    @apply h-full select-none w-[33%] absolute;
    perspective: 1200px;
    transition: 2s;
}

.env3d {
    @apply select-none absolute block z-10;
    backface-visibility: visible;
    transform-style: preserve-3d;
    transition: 2s;
}

.container3d {
    @apply absolute block top-0 left-0 visible;
    backface-visibility: visible;
    transform-style: preserve-3d;
    transform: translateZ(0px) rotateY(0deg) rotateX(0deg);
}
</style>
